import React, { useState, useEffect, useRef } from "react";
import "./login.scss";
import toast from "react-hot-toast";
import api from "../../../services/api";

const FieldError = ({ msg }: { msg?: string }) => {
    if (!msg) return null;
    return (
        <div style={{ color: '#E11D48', fontSize: '0.85rem', marginTop: '4px', display: 'flex', alignItems: 'center', gap: '4px' }}>
            <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><circle cx="12" cy="12" r="10"></circle><line x1="12" y1="8" x2="12" y2="12"></line><line x1="12" y1="16" x2="12.01" y2="16"></line></svg>
            {msg}
        </div>
    );
};

interface ForgetPasswordProps {
    onClose?: () => void;
}

const ForgetPassword = ({ onClose }: ForgetPasswordProps) => {
    const [step, setStep] = useState<1 | 2 | 3>(1);
    const [emailOrMobile, setEmailOrMobile] = useState("");
    const [fieldErrors, setFieldErrors] = useState<any>({});

    // Step 2 States
    const [otp, setOtp] = useState(["", "", "", ""]);
    const [timer, setTimer] = useState(30);
    const inputRefs = useRef<(HTMLInputElement | null)[]>([]);

    // Step 3 States
    const [password, setPassword] = useState("");
    const [confirmPassword, setConfirmPassword] = useState("");
    const [showPassword, setShowPassword] = useState(false);
    const [showConfirmPassword, setShowConfirmPassword] = useState(false);

    // Timer logic for Step 2
    useEffect(() => {
        let interval: NodeJS.Timeout;
        if (step === 2 && timer > 0) {
            interval = setInterval(() => {
                setTimer((prev) => prev - 1);
            }, 1000);
        }
        return () => clearInterval(interval);
    }, [step, timer]);

    const handleSendOtp = async (e?: React.FormEvent) => {
        if (e) e.preventDefault();
        setFieldErrors({});

        if (!emailOrMobile.trim()) {
            setFieldErrors({ emailOrMobile: "Please enter an email or mobile number." });
            toast.error("Please fill in all required fields.");
            return;
        }

        try {
            const { response, data } = await api.post("/auth/forget-password", { emailOrMobile });
            
            if (response.ok) {
                toast.success(data.message || "OTP Sent! Please check your messages.");
                console.log("OTP (For testing):", data.otp);
                setTimer(30);
                setStep(2);
            } else {
                setFieldErrors({ emailOrMobile: data.message });
                toast.error(data.message || "Failed to initiate password reset");
            }
        } catch (err) {
            toast.error("Error connecting to server.");
        }
    };

    const handleOtpChange = (index: number, value: string) => {
        if (value.length > 1) return; // Prevent pasting multiple chars inside one without split
        const newOtp = [...otp];
        newOtp[index] = value;
        setOtp(newOtp);

        if (value !== "" && index < 3) {
            inputRefs.current[index + 1]?.focus();
        }
    };

    const handleOtpKeyDown = (index: number, e: React.KeyboardEvent<HTMLInputElement>) => {
        if (e.key === "Backspace" && !otp[index] && index > 0) {
            inputRefs.current[index - 1]?.focus();
        }
    };

    const handleVerifyOtp = async () => {
        const fullOtp = otp.join("");
        if (fullOtp.length < 4) {
            toast.error("Please enter the complete 4-digit verification code.");
            return;
        }

        try {
            const { response, data } = await api.post("/auth/verify-otp", { emailOrMobile, otp: fullOtp });
            
            if (response.ok) {
                toast.success("Verification successful!");
                setStep(3);
            } else {
                toast.error(data.message || "Invalid verifying code.");
            }
        } catch (err) {
            toast.error("Error connecting to server.");
        }
    };

    const handleChangePassword = async (e: React.FormEvent) => {
        e.preventDefault();
        setFieldErrors({});
        let errors: any = {};

        if (password.length < 6) {
            errors.password = "Password must be at least 6 characters long.";
        }
        if (password !== confirmPassword) {
            errors.confirmPassword = "Passwords do not match.";
        }
        if (Object.keys(errors).length > 0) {
            setFieldErrors(errors);
            toast.error("Please fill in requirements correctly.");
            return;
        }

        try {
            const { response, data } = await api.post("/auth/change-password", { emailOrMobile, newPassword: password });
            
            if (response.ok) {
                toast.success("Password updated successfully! Please login with your new password.");
                setTimeout(() => { if (onClose) onClose(); }, 2000);
            } else {
                toast.error(data.message || "Failed to update password.");
            }
        } catch (err) {
            toast.error("Server error connecting to database.");
        }
    };

    const formatTime = (time: number) => {
        const min = Math.floor(time / 60);
        const sec = time % 60;
        return `${min.toString().padStart(2, '0')}:${sec.toString().padStart(2, '0')}`;
    };

    return (
        <div className="login-modal-backdrop" onClick={onClose}>
            <div className={`login-popup-card ${step === 3 ? 'signup-card' : 'forget-password-card'}`} onClick={e => e.stopPropagation()}>
                <button className="close-button" onClick={onClose}>
                    <svg viewBox="0 0 24 24" stroke="currentColor" strokeWidth="3" fill="none" strokeLinecap="round" strokeLinejoin="round">
                        <line x1="6" y1="6" x2="18" y2="18"></line>
                        <line x1="18" y1="6" x2="6" y2="18"></line>
                    </svg>
                </button>
                <div className="login-form-section">

                    {step === 1 && (
                        <>
                            <h3 className="text-center mt-2 mb-3">Forget Password</h3>
                            <p className="subtitle" style={{ color: "#777", fontSize: "1.05rem", lineHeight: "1.4", marginBottom: "1rem" }}>
                                Enter your Email or Mobile Number for verification. A 4-digit code will be sent to you.
                            </p>
                            <form onSubmit={handleSendOtp} className="signup-form">
                                <div className="form-group mb-3">
                                    <label htmlFor="emailOrMobile"><span className="star">* </span>Email or Mobile Number</label>
                                    <input
                                        type="text"
                                        id="emailOrMobile"
                                        placeholder="Enter Email or Mobile Number"
                                        value={emailOrMobile}
                                        onChange={e => { setEmailOrMobile(e.target.value); setFieldErrors({ ...fieldErrors, emailOrMobile: "" }); }}
                                        style={{ borderColor: fieldErrors.emailOrMobile ? '#E11D48' : undefined }}
                                        required
                                    />
                                    <FieldError msg={fieldErrors.emailOrMobile} />
                                </div>
                                <button type="submit" className="login-submit-btn forget-pwd-btn" style={{ marginTop: "2rem" }}>Continue</button>
                            </form>
                        </>
                    )}

                    {step === 2 && (
                        <div className="text-center w-100">
                            <h3 className="mt-2 mb-3 text-center">Verification</h3>
                            <p className="subtitle mx-auto" style={{ color: "#666", fontSize: "0.95rem", lineHeight: "1.4", marginBottom: "1.5rem", maxWidth: '350px' }}>
                                Enter your 4 digits code that you received on your email. <br />
                                <strong style={{ color: '#333' }}>{emailOrMobile}</strong>
                                <span style={{ color: '#4fbf9f', cursor: 'pointer', marginLeft: '8px', fontWeight: 600 }} onClick={() => setStep(1)}>Change</span>
                            </p>

                            <div className="d-flex justify-content-center gap-3 mb-4">
                                {otp.map((digit, index) => (
                                    <input
                                        key={index}
                                        ref={(el) => { inputRefs.current[index] = el; }}
                                        type="text"
                                        maxLength={1}
                                        value={digit}
                                        onChange={(e) => handleOtpChange(index, e.target.value)}
                                        onKeyDown={(e) => handleOtpKeyDown(index, e)}
                                        style={{
                                            width: "50px",
                                            height: "50px",
                                            fontSize: "1.5rem",
                                            textAlign: "center",
                                            border: "1px solid #ced4da",
                                            borderRadius: "8px",
                                            fontWeight: "bold",
                                            color: "#2C3439"
                                        }}
                                        autoFocus={index === 0}
                                    />
                                ))}
                            </div>

                            <div className="text-center mb-4" style={{ color: "#E11D48", fontWeight: "600" }}>
                                {formatTime(timer)}
                            </div>

                            <button onClick={handleVerifyOtp} className="login-submit-btn" style={{ width: '100%', marginBottom: '1.5rem' }}>Verify</button>

                            <div style={{ color: '#666', fontSize: '0.95rem' }}>
                                If you didn't receive a code! <br />
                                <span
                                    style={{
                                        color: timer === 0 ? '#E11D48' : '#bbb',
                                        fontWeight: 600,
                                        cursor: timer === 0 ? 'pointer' : 'default',
                                        display: 'inline-block',
                                        marginTop: '5px'
                                    }}
                                    onClick={() => timer === 0 && handleSendOtp()}
                                >
                                    Resend
                                </span>
                            </div>
                        </div>
                    )}

                    {step === 3 && (
                        <>
                            <h3 className="text-center mt-2 mb-3">Set A New Password</h3>
                            <p className="subtitle text-center" style={{ color: "#777", fontSize: "0.95rem", lineHeight: "1.4", marginBottom: "1.5rem" }}>
                                Set the new password for your account so you can login and access all features.
                            </p>

                            <form onSubmit={handleChangePassword} className="signup-form w-100">
                                <div className="form-group mb-3 text-start">
                                    <label htmlFor="password"><span className="star">* </span>Password</label>
                                    <div className="input-with-icon">
                                        <input
                                            type={showPassword ? "text" : "password"}
                                            id="password"
                                            placeholder="Enter a Password"
                                            value={password}
                                            onChange={e => { setPassword(e.target.value); setFieldErrors({ ...fieldErrors, password: "" }) }}
                                            style={{ borderColor: fieldErrors.password ? '#E11D48' : undefined }}
                                            required
                                        />
                                        <svg className="field-icon eye-icon" onClick={() => setShowPassword(!showPassword)} viewBox="0 0 24 24" fill="none" stroke="#263238" strokeWidth="2" style={{ cursor: "pointer" }}>
                                            {showPassword ? (
                                                <><path d="M1 12s4-8 11-8 11 8 11 8-4 8-11 8-11-8-11-8z"></path><circle cx="12" cy="12" r="3"></circle></>
                                            ) : (
                                                <><path d="M17.94 17.94A10.07 10.07 0 0 1 12 20c-7 0-11-8-11-8a18.45 18.45 0 0 1 5.06-5.94M9.9 4.24A9.12 9.12 0 0 1 12 4c7 0 11 8 11 8a18.5 18.5 0 0 1-2.16 3.19m-6.72-1.07a3 3 0 1 1-4.24-4.24"></path><line x1="1" y1="1" x2="23" y2="23"></line></>
                                            )}
                                        </svg>
                                    </div>
                                    <FieldError msg={fieldErrors.password} />
                                </div>

                                <div className="form-group mb-4 text-start">
                                    <label htmlFor="confirmPassword"><span className="star">* </span>Confirm Password</label>
                                    <div className="input-with-icon">
                                        <input
                                            type={showConfirmPassword ? "text" : "password"}
                                            id="confirmPassword"
                                            placeholder="Enter a Confirm Password"
                                            value={confirmPassword}
                                            onChange={e => { setConfirmPassword(e.target.value); setFieldErrors({ ...fieldErrors, confirmPassword: "" }) }}
                                            style={{ borderColor: fieldErrors.confirmPassword ? '#E11D48' : undefined }}
                                            required
                                        />
                                        <svg className="field-icon eye-icon" onClick={() => setShowConfirmPassword(!showConfirmPassword)} viewBox="0 0 24 24" fill="none" stroke="#263238" strokeWidth="2" style={{ cursor: "pointer" }}>
                                            {showConfirmPassword ? (
                                                <><path d="M1 12s4-8 11-8 11 8 11 8-4 8-11 8-11-8-11-8z"></path><circle cx="12" cy="12" r="3"></circle></>
                                            ) : (
                                                <><path d="M17.94 17.94A10.07 10.07 0 0 1 12 20c-7 0-11-8-11-8a18.45 18.45 0 0 1 5.06-5.94M9.9 4.24A9.12 9.12 0 0 1 12 4c7 0 11 8 11 8a18.5 18.5 0 0 1-2.16 3.19m-6.72-1.07a3 3 0 1 1-4.24-4.24"></path><line x1="1" y1="1" x2="23" y2="23"></line></>
                                            )}
                                        </svg>
                                    </div>
                                    <FieldError msg={fieldErrors.confirmPassword} />
                                </div>

                                <button type="submit" className="login-submit-btn" style={{ width: '100%' }}>Update Password</button>
                            </form>
                        </>
                    )}
                </div>
            </div>
        </div>
    );
};

export default ForgetPassword;
